import type { Dispatch, SetStateAction } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CostCentre, DayKey, Project, Timesheet } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { ApprovalDay } from '@/utils/ApprovalDashboard/dayHelpers';
import { isWeekendDay } from '@/utils/ApprovalDashboard/dayHelpers';
import type { EditedEntries, SelectionLabel } from '@/hooks/ApprovalDashboard/useTimesheetReviewEdit';
import type { BookingDetails } from './TimesheetReviewRow';
import { TimesheetReviewRow } from './TimesheetReviewRow';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export interface TimesheetReviewTableProps {
  timesheet: Timesheet | null;
  days: ApprovalDay[];
  dayLabels: string[];
  isEditMode: boolean;
  isDaily: boolean;
  orgSettings: OrganisationTimesheetSettings;
  overtimeEnabled: boolean;
  overtimeTypes: Array<{ id: number; name: string; badgeColour: string }>;
  effectiveProjects: Project[];
  selectableProjects: Project[];
  /** Cost centres valid for a project, keyed by project code. */
  costCentresByProjectCode: Record<string, CostCentre[]>;
  currentCompanyId?: string;
  screenMode: 'demo' | 'live';
  editedEntries: EditedEntries;
  editingHours: Record<string, string | undefined>;
  setEditingHours: Dispatch<SetStateAction<Record<string, string | undefined>>>;
  getHoursCellKey: (entryId: number, day: ApprovalDay) => string;
  expandedEntryIds: Set<number>;
  toggleEntryExpansion: (entryId: number) => void;
  shouldShowExpansionIcon: (description: string) => boolean;
  handleEntryFieldChange: (entryId: number, field: 'projectId' | 'costCentreId' | 'description', value: string) => void;
  handleEntryProjectChange: (entryId: number, projectId: string, project?: SelectionLabel) => void;
  handleEntryCostCentreChange: (entryId: number, costCentreId: string, costCentre?: SelectionLabel) => void;
  handleEntryRoleChange: (entryId: number, roleId: number | undefined, roleName?: string) => void;
  handleEntryHoursChange: (entryId: number, day: DayKey, value: string) => void;
  handleDailyNoteChange: (entryId: number, day: DayKey, value: string) => void;
  onViewBookingDetails: (booking: BookingDetails) => void;
  computedDailyTotals: Record<DayKey, number> | null;
}

export function TimesheetReviewTable({
  timesheet,
  days,
  dayLabels,
  isEditMode,
  isDaily,
  orgSettings,
  overtimeEnabled,
  overtimeTypes,
  effectiveProjects,
  selectableProjects,
  costCentresByProjectCode,
  currentCompanyId,
  screenMode,
  editedEntries,
  editingHours,
  setEditingHours,
  getHoursCellKey,
  expandedEntryIds,
  toggleEntryExpansion,
  shouldShowExpansionIcon,
  handleEntryFieldChange,
  handleEntryProjectChange,
  handleEntryCostCentreChange,
  handleEntryRoleChange,
  handleEntryHoursChange,
  handleDailyNoteChange,
  onViewBookingDetails,
  computedDailyTotals,
}: TimesheetReviewTableProps) {
  // Derive whether any entry has an HTC value — drives banner visibility
  const hasAnyHtc = (timesheet?.entries ?? []).some(e => e.hoursToComplete != null);
  const { hoursToComplete } = useFeatureFlags();
  const showHtcColumn = hoursToComplete && orgSettings?.showHoursToComplete === true;

  return (
    <div className="space-y-3">
      {/* ── HTC Callout Banner ─────────────────────────────────────────────────
           Shown when the setting is on AND at least one entry carries an HTC value.
           Amber/informational — not an error. */}
      {showHtcColumn && hasAnyHtc && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-300 font-medium">
          <Clock className="h-4 w-4 flex-shrink-0 text-amber-500 dark:text-amber-400" />
          <span>This timesheet includes Hours to Complete estimates — review before approving</span>
        </div>
      )}

      <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="p-2 text-left">Project</th>
            <th className="p-2 text-left">Cost Centre</th>
            {orgSettings?.showRole && (
              <th className="p-2 text-left">
                Role{orgSettings?.requireRole && <span className="text-destructive ml-0.5">*</span>}
              </th>
            )}
            {!isDaily && <th className="p-2 text-left">Description</th>}
            {isDaily && <th className="p-2 text-left">Notes</th>}
            {days.map((day, i) => (
              <th
                key={day}
                className={cn(
                  'p-2 text-center',
                  isWeekendDay(day) && 'bg-muted'
                )}
              >
                {dayLabels[i]}
              </th>
            ))}
            {overtimeEnabled && (
              <th className="p-2 text-left">Overtime Type</th>
            )}
            {showHtcColumn && (
              <th className="p-2 text-center whitespace-nowrap">Hrs remaining</th>
            )}
            <th className="p-2 text-center">Total</th>
          </tr>
        </thead>
        <tbody>
          {(timesheet?.entries ?? []).map(entry => (
            <TimesheetReviewRow
              key={entry.id}
              entry={entry}
              entryEdits={editedEntries[entry.id] || {}}
              days={days}
              isEditMode={isEditMode}
              isDaily={isDaily}
              orgSettings={orgSettings}
              overtimeEnabled={overtimeEnabled}
              overtimeTypes={overtimeTypes}
              effectiveProjects={effectiveProjects}
              selectableProjects={selectableProjects}
              costCentresByProjectCode={costCentresByProjectCode}
              currentCompanyId={currentCompanyId}
              employeeId={timesheet?.employeeId}
              screenMode={screenMode}
              isExpanded={expandedEntryIds.has(entry.id)}
              editingHours={editingHours}
              setEditingHours={setEditingHours}
              getHoursCellKey={getHoursCellKey}
              toggleEntryExpansion={toggleEntryExpansion}
              shouldShowExpansionIcon={shouldShowExpansionIcon}
              handleEntryFieldChange={handleEntryFieldChange}
              handleEntryProjectChange={handleEntryProjectChange}
              handleEntryCostCentreChange={handleEntryCostCentreChange}
              handleEntryRoleChange={handleEntryRoleChange}
              handleEntryHoursChange={handleEntryHoursChange}
              handleDailyNoteChange={handleDailyNoteChange}
              onViewBookingDetails={onViewBookingDetails}
            />
          ))}

          {/* Daily Totals Row — live-recalculates as hours are edited */}
          {computedDailyTotals && (
            <tr className="border-t-2 border-t-primary bg-accent-teal-50/50 dark:bg-accent-teal-950/20 font-semibold">
              {/* Span the same fixed left columns as the header: Project + Cost Centre + (Role?) + Description/Notes */}
              <td colSpan={
                (orgSettings?.showRole ? 3 : 2) + 1
              } className="p-2 text-left">
                <span className="font-bold">Daily Totals</span>
              </td>
              {days.map(day => {
                const weekend = isWeekendDay(day);
                return (
                  <td key={day} className={cn('p-2 text-center', weekend && 'bg-muted/30')}>
                    {computedDailyTotals[day] !== 0 ? (
                      weekend ? (
                        <span className="text-orange-600 font-bold">{computedDailyTotals[day].toFixed(2)}</span>
                      ) : (
                        computedDailyTotals[day].toFixed(2)
                      )
                    ) : '-'}
                  </td>
                );
              })}
              {overtimeEnabled && (
                <td className="p-2"></td>
              )}
              {showHtcColumn && (
                <td className="p-2 text-center text-muted-foreground">—</td>
              )}
              <td className="p-2 text-center font-bold text-accent-teal-900">
                {days.reduce((s, day) => s + (computedDailyTotals[day] ?? 0), 0).toFixed(2)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
